import { create } from 'zustand'
import {UserScope} from "../api/ga/bo/user_scope/user_scope_pb";
import {
  buildScopeIndex,
  emptyScopeIndex,
  Permission,
  ScopeIndex,
  hasAllPermissions,
  hasCasinoPermission,
  hasPermission,
  hasProviderAccess,
} from './permissions'

export interface StateUserScope {
  userScope?: UserScope
  // Derived from userScope, always updated together with it.
  scopeIndex: ScopeIndex
  setUserScope: (userScope: UserScope) => void
}
const defaultStateUserScope: StateUserScope = {
  userScope: undefined,
  scopeIndex: emptyScopeIndex,
  setUserScope(userScope: UserScope): void {},
}

export interface StatePreload {
  loaded?: boolean
  setLoaded: (loaded: boolean) => void
}
const defaultStatePreload: StatePreload = {
  loaded: false,
  setLoaded(loaded: boolean): void {},
}

export const useMainStates = create<
  StateUserScope &
    StatePreload
>((set) => ({
  ...defaultStateUserScope,
  ...defaultStatePreload,
  setUserScope: (userScope: UserScope) =>
    set({ userScope: userScope, scopeIndex: buildScopeIndex(userScope) }),
  setLoaded: (loaded: boolean) => set({ loaded: loaded }),
}))

// Returns true when the user has at least one of the given permissions.
export const useHasPermission = (...permissions: Permission[]): boolean =>
  useMainStates((state) => hasPermission(state.scopeIndex, ...permissions))

// Returns true when the user has every one of the given permissions.
export const useHasAllPermissions = (...permissions: Permission[]): boolean =>
  useMainStates((state) => hasAllPermissions(state.scopeIndex, ...permissions))

// Casino-scoped check: the permission itself plus access to that casino.
export const useHasCasinoPermission = (
  permission: Permission,
  casinoId: string
): boolean =>
  useMainStates((state) =>
    hasCasinoPermission(state.scopeIndex, permission, casinoId)
  )

export const useHasProviderAccess = (providerName: string): boolean =>
  useMainStates((state) => hasProviderAccess(state.scopeIndex, providerName))

// Escape hatch for checks outside of React render (callbacks, api layer).
export const checkPermission = (...permissions: Permission[]): boolean =>
  hasPermission(useMainStates.getState().scopeIndex, ...permissions)